import { closeFeedbackModal, collapseSmartSnippet, dislikeSmartSnippet, expandSmartSnippet, likeSmartSnippet, openFeedbackModal, } from '../../../features/question-answering/question-answering-actions.js';
import { answerSourceSelector } from '../../../features/question-answering/question-answering-selectors.js';
import { questionAnsweringReducer as questionAnswering } from '../../../features/question-answering/question-answering-slice.js';
import { pushRecentResult } from '../../../features/recent-results/recent-results-actions.js';
import { searchReducer as search } from '../../../features/search/search-slice.js';
import { loadReducerError } from '../../../utils/errors.js';
import { buildController, } from '../../controller/headless-controller.js';
import { buildInteractiveResultCore } from '../interactive-result/headless-core-interactive-result.js';
/**
 * Creates a `SmartSnippet` controller instance.
 *
 * @param engine - The headless engine.
 * @param analyticsClient - A SmartSnippetAnalyticsClient to send the appropriate analytics calls.
 * @param props - The configurable `SmartSnippet` properties.
 * @returns A `SmartSnippetCore` controller instance.
 * */
export function buildCoreSmartSnippet(engine, analyticsClient, props) {
    if (!loadSmartSnippetReducers(engine)) {
        throw loadReducerError;
    }
    const controller = buildController(engine);
    const getState = () => engine.state;
    const getResult = () => answerSourceSelector(getState());
    let lastSearchResponseId = null;
    const interactiveResult = buildInteractiveResultCore(engine, { options: { selectionDelay: props?.options?.selectionDelay } }, () => {
        const result = getResult();
        if (!result) {
            lastSearchResponseId = null;
            return;
        }
        const { searchResponseId } = getState().search;
        if (lastSearchResponseId === searchResponseId) {
            return;
        }
        lastSearchResponseId = searchResponseId;
        engine.dispatch(analyticsClient.logOpenSmartSnippetSource());
        engine.dispatch(pushRecentResult(result));
    });
    return {
        ...controller,
        get state() {
            const state = getState();
            return {
                question: state.search.questionAnswer.question,
                answer: state.search.questionAnswer.answerSnippet,
                documentId: state.search.questionAnswer.documentId,
                expanded: state.questionAnswering.expanded,
                answerFound: state.search.questionAnswer.answerSnippet !== '',
                liked: state.questionAnswering.liked,
                disliked: state.questionAnswering.disliked,
                feedbackModalOpen: state.questionAnswering.feedbackModalOpen,
                source: getResult(),
            };
        },
        expand() {
            engine.dispatch(analyticsClient.logExpandSmartSnippet());
            engine.dispatch(expandSmartSnippet());
        },
        collapse() {
            engine.dispatch(analyticsClient.logCollapseSmartSnippet());
            engine.dispatch(collapseSmartSnippet());
        },
        like() {
            engine.dispatch(analyticsClient.logLikeSmartSnippet());
            engine.dispatch(likeSmartSnippet());
        },
        dislike() {
            engine.dispatch(analyticsClient.logDislikeSmartSnippet());
            engine.dispatch(dislikeSmartSnippet());
        },
        openFeedbackModal() {
            engine.dispatch(analyticsClient.logOpenSmartSnippetFeedbackModal());
            engine.dispatch(openFeedbackModal());
        },
        closeFeedbackModal() {
            engine.dispatch(analyticsClient.logCloseSmartSnippetFeedbackModal());
            engine.dispatch(closeFeedbackModal());
        },
        sendFeedback(feedback) {
            engine.dispatch(analyticsClient.logSmartSnippetFeedback(feedback));
            engine.dispatch(closeFeedbackModal());
        },
        sendDetailedFeedback(details) {
            engine.dispatch(analyticsClient.logSmartSnippetDetailedFeedback(details));
            engine.dispatch(closeFeedbackModal());
        },
        selectSource() {
            interactiveResult.select();
        },
        beginDelayedSelectSource() {
            interactiveResult.beginDelayedSelect();
        },
        cancelPendingSelectSource() {
            interactiveResult.cancelPendingSelect();
        },
    };
}
function loadSmartSnippetReducers(engine) {
    engine.addReducers({ search, questionAnswering });
    return true;
}
